import React, { useState, useEffect } from 'react';
import { Wallet, ArrowUpRight, ArrowDownLeft, Clock, CheckCircle, XCircle, Search, Filter, RefreshCw, PlusCircle, MinusCircle, ShieldAlert } from 'lucide-react';
import { walletsAPI } from '../utils/api';

const WalletManagement = () => {
  const [activeTab, setActiveTab] = useState('wallets'); // 'wallets' or 'ledger'
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total_balance: 0, total_wallets: 0, total_added: 0 });
  const [wallets, setWallets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [isAdjustModalOpen, setIsAdjustModalOpen] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [adjustType, setAdjustType] = useState('ADD'); // 'ADD' or 'DEDUCT'
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustLoading, setAdjustLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 1. Fetch Stats
      const [statsRes, txStatsRes] = await Promise.all([
        walletsAPI.getWalletStats(),
        walletsAPI.getTransactionStats()
      ]);
      
      const statsData = statsRes.data?.data || {};
      const txStatsData = txStatsRes.data?.data || [];
      const addCoinsItem = txStatsData.find(item => item.transaction_type === 'COIN_ADD');
      
      setStats({
        total_balance: Number(statsData.total_balance) || 0,
        total_wallets: Number(statsData.total_wallets) || 0,
        total_added: Number(addCoinsItem?.total_amount) || 0
      });

      // 2. Fetch Tab Content
      if (activeTab === 'wallets') {
        const walletsRes = await walletsAPI.getWallets();
        setWallets(walletsRes.data?.data || []);
      } else {
        const txsRes = await walletsAPI.getAllTransactions();
        setTransactions(txsRes.data?.data || []);
      }
    } catch (err) {
      console.error("Error loading wallet details:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdjustModal = (wallet, type) => {
    setSelectedWallet(wallet);
    setAdjustType(type);
    setAdjustAmount('');
    setAdjustReason('');
    setIsAdjustModalOpen(true);
  };

  const handleAdjustBalance = async (e) => {
    e.preventDefault();
    if (!selectedWallet || !adjustAmount || Number(adjustAmount) <= 0) return;
    
    setAdjustLoading(true);
    try {
      const payload = {
        amount: Number(adjustAmount),
        reason: adjustReason.trim() || `Admin balance adjustment (${adjustType})`
      };
      
      let res;
      if (adjustType === 'ADD') {
        res = await walletsAPI.addCoins(selectedWallet.user_id, payload);
      } else {
        res = await walletsAPI.deductCoins(selectedWallet.user_id, payload);
      }
      
      if (res.data.success) {
        setIsAdjustModalOpen(false);
        fetchData();
      }
    } catch (err) {
      alert("Adjustment failed: " + (err.response?.data?.message || err.message));
    } finally {
      setAdjustLoading(false);
    }
  };

  // Filters
  const filteredWallets = wallets.filter(w => 
    (w.user_name && w.user_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (w.email && w.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (w.user_id && w.user_id.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const filteredTransactions = transactions.filter(t => 
    (t.user_name && t.user_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (t.transaction_type && t.transaction_type.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (t.reason && t.reason.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (t.id && t.id.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800">Wallet & Coins Management</h2>
          <p className="text-slate-500 mt-1">Audit user balances, monitor coin transaction logs, and perform manual adjustments.</p>
        </div>
        <button 
          onClick={fetchData}
          className="flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition active:scale-95 shadow-sm"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh Ledger
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-6 rounded-3xl text-white shadow-xl shadow-slate-300/40 border border-slate-700/50">
          <div className="flex justify-between items-start mb-4">
            <div className="p-3 bg-white/10 rounded-xl"><Wallet size={24} className="text-pink-500" /></div>
            <span className="text-xs font-bold bg-pink-500/20 text-pink-400 px-2.5 py-1 rounded-full">CIRCULATING</span>
          </div>
          <p className="text-gray-400 text-sm font-semibold">Total Coins Circulating</p>
          <h3 className="text-4xl font-extrabold mt-1 tracking-tight">{stats.total_balance.toLocaleString()} <span className="text-sm font-medium text-pink-400">COINS</span></h3>
        </div>

        <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex items-center justify-between">
          <div>
            <p className="text-slate-500 text-sm font-semibold">Total User Wallets</p>
            <h3 className="text-3xl font-extrabold text-slate-800 mt-1">{stats.total_wallets.toLocaleString()}</h3>
            <p className="text-slate-400 text-xs font-semibold mt-2 flex items-center gap-1">
              Active Wallets in Database
            </p>
          </div>
          <div className="h-16 w-16 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-700">
            <PlusCircle size={32} />
          </div>
        </div>

        <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex items-center justify-between">
          <div>
            <p className="text-slate-500 text-sm font-semibold">Total Admin Coins Issued</p>
            <h3 className="text-3xl font-extrabold text-slate-800 mt-1">{stats.total_added.toLocaleString()}</h3>
            <p className="text-green-500 text-xs font-bold mt-2 flex items-center gap-1">
              <ArrowDownLeft size={12} /> Via COIN_ADD transactions
            </p>
          </div>
          <div className="h-16 w-16 bg-green-50 rounded-2xl flex items-center justify-center text-green-500">
            <ArrowDownLeft size={32} />
          </div>
        </div>
      </div>

      {/* Main Table Container */}
      <div className="bg-white rounded-[24px] shadow-xl shadow-slate-100/30 border border-slate-100 overflow-hidden">
        
        {/* Toolbar & Tabs */}
        <div className="p-6 border-b border-slate-100 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex bg-slate-100 p-1 rounded-xl">
            <button 
              onClick={() => setActiveTab('wallets')}
              className={`px-5 py-2.5 rounded-lg text-xs font-bold transition-all ${activeTab === 'wallets' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              User Wallets
            </button>
            <button 
              onClick={() => setActiveTab('ledger')}
              className={`px-5 py-2.5 rounded-lg text-xs font-bold transition-all ${activeTab === 'ledger' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              System Ledger (Transactions)
            </button>
          </div>
          
          <div className="flex gap-3 w-full md:w-auto">
             <div className="relative w-full md:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input 
                  type="text" 
                  placeholder={activeTab === 'wallets' ? "Search by user, email, or ID..." : "Search ledger entries..."} 
                  className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/20 transition" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
             </div>
          </div>
        </div>

        {/* Content Table */}
        {loading ? (
          <div className="p-20 text-center">
            <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-500">Retrieving records...</p>
          </div>
        ) : activeTab === 'wallets' ? (
          /* USER WALLETS LIST */
          filteredWallets.length === 0 ? (
            <div className="p-20 text-center text-slate-400">
              <Wallet size={48} className="mx-auto mb-4 opacity-30" />
              <p>No wallets found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                    <th className="p-5">User Details</th>
                    <th className="p-5">User ID / Email</th>
                    <th className="p-5">Balance</th>
                    <th className="p-5">Last Modified</th>
                    <th className="p-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {filteredWallets.map((w) => (
                    <tr key={w.user_id} className="hover:bg-slate-50/50 transition">
                      <td className="p-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-pink-50 text-pink-600 flex items-center justify-center font-bold">
                            {w.user_name?.[0] || 'U'}
                          </div>
                          <div>
                            <p className="font-bold text-slate-800">{w.user_name || 'Anonymous User'}</p>
                            <p className="text-[10px] text-slate-400">Tenant: {w.tenantcode || 'default'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-5">
                        <p className="text-slate-600 font-semibold">{w.email}</p>
                        <p className="text-xs text-slate-400">ID: {w.user_id}</p>
                      </td>
                      <td className="p-5 font-bold text-slate-800 text-base">
                        {Number(w.balance || 0).toLocaleString()} Coins
                      </td>
                      <td className="p-5 text-slate-500 text-xs">
                        {w.lastmodified_at ? new Date(w.lastmodified_at).toLocaleString() : 'N/A'}
                      </td>
                      <td className="p-5 text-right">
                        <div className="flex justify-end gap-2">
                          <button 
                            onClick={() => handleOpenAdjustModal(w, 'ADD')}
                            className="bg-green-50 text-green-600 border border-green-100 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-green-100 active:scale-95 transition"
                          >
                            Add Coins
                          </button>
                          <button 
                            onClick={() => handleOpenAdjustModal(w, 'DEDUCT')}
                            className="bg-red-50 text-red-600 border border-red-100 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-red-100 active:scale-95 transition"
                          >
                            Deduct
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          /* SYSTEM LEDGER (TRANSACTIONS) */
          filteredTransactions.length === 0 ? (
            <div className="p-20 text-center text-slate-400">
              <Clock size={48} className="mx-auto mb-4 opacity-30" />
              <p>No transaction logs recorded.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                    <th className="p-5">Transaction ID / Date</th>
                    <th className="p-5">User</th>
                    <th className="p-5">Type</th>
                    <th className="p-5">Coins Delta</th>
                    <th className="p-5">Wallet Path</th>
                    <th className="p-5">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {filteredTransactions.map((tx) => {
                    const isAdd = tx.transaction_type === 'COIN_ADD';
                    return (
                      <tr key={tx.id} className="hover:bg-slate-50/50 transition">
                        <td className="p-5">
                          <p className="font-bold text-slate-800">TXN-{tx.id.substring(0, 8).toUpperCase()}</p>
                          <p className="text-[10px] text-slate-400">{new Date(tx.created_at || tx.timestamp).toLocaleString()}</p>
                        </td>
                        <td className="p-5 text-slate-700 font-bold">
                          {tx.user_name || `ID: ${tx.user_id?.substring(0, 8)}`}
                        </td>
                        <td className="p-5">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold uppercase ${
                            isAdd ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'
                          }`}>
                            {isAdd ? <ArrowDownLeft size={12} /> : <ArrowUpRight size={12} />}
                            {tx.transaction_type}
                          </span>
                        </td>
                        <td className={`p-5 font-bold text-base ${isAdd ? 'text-green-600' : 'text-slate-800'}`}>
                          {isAdd ? '+' : '-'}{Number(tx.amount).toLocaleString()}
                        </td>
                        <td className="p-5 text-xs text-slate-500 font-semibold">
                          {Number(tx.balance_before || 0).toLocaleString()} → {Number(tx.balance_after || 0).toLocaleString()}
                        </td>
                        <td className="p-5 text-slate-500 text-xs italic font-medium max-w-[200px] truncate" title={tx.reason}>
                          {tx.reason || 'None specified'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {/* Adjust Balance Modal */}
      {isAdjustModalOpen && selectedWallet && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
              <h3 className="text-lg font-bold text-slate-800">
                {adjustType === 'ADD' ? 'Issue / Add Coins' : 'Deduct / Revoke Coins'}
              </h3>
              <button 
                onClick={() => setIsAdjustModalOpen(false)} 
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-50 transition"
              >
                <XCircle size={20} />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleAdjustBalance} className="p-6 space-y-4">
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 text-xs">
                <p className="font-bold text-slate-700 mb-1">Target Account:</p>
                <p className="text-slate-600">{selectedWallet.user_name} ({selectedWallet.email})</p>
                <p className="font-bold text-pink-600 mt-2">Current Balance: {Number(selectedWallet.balance || 0).toLocaleString()} Coins</p>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-500 uppercase">Coin Amount</label>
                <input
                  required
                  type="number"
                  placeholder="e.g. 5000"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition font-bold"
                  value={adjustAmount}
                  onChange={(e) => setAdjustAmount(e.target.value)}
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-500 uppercase">Reason / Audit Note</label>
                <textarea
                  required
                  rows={3}
                  placeholder="Explain why this adjustment is being made..."
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition text-sm"
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsAdjustModalOpen(false)}
                  className="px-5 py-2.5 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={adjustLoading}
                  className={`flex items-center gap-2 px-6 py-2.5 text-white rounded-xl text-xs font-bold transition active:scale-95 shadow-lg ${
                    adjustType === 'ADD' 
                      ? 'bg-green-500 hover:bg-green-600 shadow-green-200' 
                      : 'bg-red-500 hover:bg-red-600 shadow-red-200'
                  } disabled:opacity-50`}
                >
                  {adjustLoading ? 'Applying...' : adjustType === 'ADD' ? 'Credit Coins' : 'Debit Coins'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletManagement;